/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {ensureExtensionsIds} from './identifiers-extensions.js'
import {EnsureDeploymentIdsPresenceOptions, LocalSource, RemoteSource} from './identifiers.js'
import {versionDiffByVersion} from '../release/version-diff.js'
import {AppVersionsDiffExtensionSchema} from '../../api/graphql/app_versions_diff.js'
import {AppInterface, CurrentAppConfiguration, filterNonVersionedAppFields} from '../../models/app/app.js'
import {MinimalOrganizationApp} from '../../models/organization.js'
import {IdentifiersExtensions} from '../../models/app/identifiers.js'
import {
  extensionTypeStrategy,
  fetchAppRemoteConfiguration,
  remoteAppConfigurationExtensionContent,
} from '../app/select-app.js'
import {AppVersion, AppModuleVersion, DeveloperPlatformClient} from '../../utilities/developer-platform-client.js'
import {
  AllAppExtensionRegistrationsQuerySchema,
  RemoteExtensionRegistrations,
} from '../../api/graphql/all_app_extension_registrations.js'
import {ExtensionSpecification} from '../../models/extensions/specification.js'
import {rewriteConfiguration} from '../app/write-app-configuration-file.js'
import {AppConfigurationUsedByCli} from '../../models/extensions/specifications/types/app_config.js'
import {removeTrailingSlash} from '../../models/extensions/specifications/validation/common.js'
import {throwUidMappingError} from '../../prompts/uid-mapping-error.js'
import {deepCompare, deepDifference} from '@shopify/cli-kit/common/object'
import {encodeToml} from '@shopify/cli-kit/node/toml'
import {zod} from '@shopify/cli-kit/node/schema'

export interface ConfigExtensionIdentifiersBreakdown {
  existingFieldNames: string[]
  existingUpdatedFieldNames: string[]
  newFieldNames: string[]
  deletedFieldNames: string[]
}

export interface ExtensionIdentifierBreakdownInfo {
  title: string
  uid: string | undefined
  experience: 'extension' | 'dashboard'
}

export function buildExtensionBreakdownInfo(title: string, uid: string | undefined): ExtensionIdentifierBreakdownInfo {
  return {title, uid, experience: 'extension'}
}

export function buildDashboardBreakdownInfo(title: string): ExtensionIdentifierBreakdownInfo {
  return {title, uid: undefined, experience: 'dashboard'}
}

export interface ExtensionIdentifiersBreakdown {
  onlyRemote: ExtensionIdentifierBreakdownInfo[]
  toCreate: ExtensionIdentifierBreakdownInfo[]
  toUpdate: ExtensionIdentifierBreakdownInfo[]
  unchanged: ExtensionIdentifierBreakdownInfo[]
}

export async function extensionsIdentifiersDeployBreakdown(options: EnsureDeploymentIdsPresenceOptions): Promise<{
  extensionIdentifiersBreakdown: ExtensionIdentifiersBreakdown
  extensionsToConfirm: {
    validMatches: IdentifiersExtensions
    extensionsToCreate: LocalSource[]
    dashboardOnlyExtensions: RemoteSource[]
  }
  remoteExtensionsRegistrations: RemoteExtensionRegistrations
}> {
  let remoteExtensionsRegistrations = await fetchRemoteExtensionsRegistrations(options)

  if (options.force && !options.developerPlatformClient.supportsDashboardManagedExtensions) {
    const unMigratedextensions = remoteExtensionsRegistrations.app.extensionRegistrations.filter((ext) => !ext.id)

    if (unMigratedextensions.length > 0) {
      throwUidMappingError()
    }
  }

  const extensionsToConfirm = await ensureExtensionsIds(options, remoteExtensionsRegistrations.app)

  if (extensionsToConfirm.didMigrateDashboardExtensions) {
    // If we migrated dashboard extensions, we need to refetch the active app version
    const newActiveAppVersion = await options.developerPlatformClient.activeAppVersion(options.remoteApp)
    // eslint-disable-next-line require-atomic-updates
    options.activeAppVersion = newActiveAppVersion
  }

  if (extensionsToConfirm.dashboardOnlyExtensions.length > 0) {
    remoteExtensionsRegistrations = await fetchRemoteExtensionsRegistrations({...options, activeAppVersion: undefined})
  }
  let extensionIdentifiersBreakdown = loadLocalExtensionsIdentifiersBreakdown(extensionsToConfirm)
  if (options.release) {
    extensionIdentifiersBreakdown =
      (await resolveRemoteExtensionIdentifiersBreakdown(
        options.developerPlatformClient,
        options.remoteApp,
        extensionsToConfirm.validMatches,
        extensionsToConfirm.extensionsToCreate,
        extensionsToConfirm.dashboardOnlyExtensions,
        options.app.specifications ?? [],
        options.activeAppVersion,
      )) ?? extensionIdentifiersBreakdown
  }
  return {
    extensionIdentifiersBreakdown,
    extensionsToConfirm,
    remoteExtensionsRegistrations: remoteExtensionsRegistrations.app,
  }
}

export async function extensionsIdentifiersReleaseBreakdown(
  developerPlatformClient: DeveloperPlatformClient,
  app: MinimalOrganizationApp,
  version: string,
) {
  const {versionsDiff, versionDetails} = await versionDiffByVersion(app, version, developerPlatformClient)

  const mapIsExtension = (extensions: AppVersionsDiffExtensionSchema[]) =>
    extensions
      .filter(
        (extension) =>
          extension.specification.experience === 'extension' &&
          extension.specification.identifier !== 'webhook_subscription',
      )
      .map((extension) => buildExtensionBreakdownInfo(extension.registrationTitle, undefined))
  const mapIsDashboard = (extensions: AppVersionsDiffExtensionSchema[]) =>
    extensions
      .filter((extension) => extension.specification.options.managementExperience === 'dashboard')
      .map((extension) => buildDashboardBreakdownInfo(extension.registrationTitle))

  const extensionIdentifiersBreakdown = {
    onlyRemote: [...mapIsExtension(versionsDiff.removed), ...mapIsDashboard(versionsDiff.removed)],
    toCreate: [...mapIsExtension(versionsDiff.added), ...mapIsDashboard(versionsDiff.added)],
    toUpdate: [],
    unchanged: [...mapIsExtension(versionsDiff.updated), ...mapIsDashboard(versionsDiff.updated)],
  }

  return {extensionIdentifiersBreakdown, versionDetails}
}

export async function configExtensionsIdentifiersBreakdown({
  developerPlatformClient,
  remoteApp,
  localApp,
  versionAppModules,
  release,
  activeAppVersion,
}: {
  developerPlatformClient: DeveloperPlatformClient
  apiKey: string
  remoteApp: MinimalOrganizationApp
  localApp: AppInterface
  versionAppModules?: AppModuleVersion[]
  release?: boolean
  activeAppVersion?: AppVersion
}) {
  if (localApp.allExtensions.filter((extension) => extension.isAppConfigExtension).length === 0) return
  if (!release) return loadLocalConfigExtensionIdentifiersBreakdown(localApp)

  return resolveRemoteConfigExtensionIdentifiersBreakdown(
    developerPlatformClient,
    remoteApp,
    localApp,
    versionAppModules,
    activeAppVersion,
  )
}

function loadLocalConfigExtensionIdentifiersBreakdown(app: AppInterface): ConfigExtensionIdentifiersBreakdown {
  return {
    existingFieldNames: filterNonVersionedAppFields(app.configuration),
    existingUpdatedFieldNames: [] as string[],
    newFieldNames: [] as string[],
    deletedFieldNames: [] as string[],
  }
}

async function fetchRemoteExtensionsRegistrations(
  options: EnsureDeploymentIdsPresenceOptions,
): Promise<AllAppExtensionRegistrationsQuerySchema> {
  return options.developerPlatformClient.appExtensionRegistrations(options.remoteApp, options.activeAppVersion)
}

async function resolveRemoteConfigExtensionIdentifiersBreakdown(
  developerPlatformClient: DeveloperPlatformClient,
  remoteApp: MinimalOrganizationApp,
  app: AppInterface,
  versionAppModules?: AppModuleVersion[],
  activeAppVersion?: AppVersion,
) {
  const remoteConfig: Partial<AppConfigurationUsedByCli> =
    (await fetchAppRemoteConfiguration(
      remoteApp,
      developerPlatformClient,
      app.specifications ?? [],
      app.remoteFlags,
      activeAppVersion,
    )) ?? {}
  const baselineConfig = (
    versionAppModules
      ? remoteAppConfigurationExtensionContent(versionAppModules, app.specifications ?? [], app.remoteFlags)
      : app.configuration
  ) as CurrentAppConfiguration

  // modify relative path webhook subscriptions to include app url so client vs server diff checking is consistent
  if (baselineConfig?.webhooks?.subscriptions?.length) {
    baselineConfig.webhooks.subscriptions = baselineConfig.webhooks.subscriptions.map((subscription) => {
      if (subscription.uri.startsWith('/')) {
        subscription.uri = `${removeTrailingSlash(baselineConfig.application_url)}${subscription.uri}`
      }
      return subscription
    })

    // Sort webhook subscriptions by uri & topics in both baseline and remote config
    // Ensuring that deepCompare will not fail if the order is different
    baselineConfig.webhooks.subscriptions.sort((webhookA, webhookB) => {
      const keyA = webhookA.uri + (webhookA.topics?.sort().join(',') ?? '')
      const keyB = webhookB.uri + (webhookB.topics?.sort().join(',') ?? '')
      return keyA.localeCompare(keyB)
    })
    remoteConfig.webhooks?.subscriptions?.sort((webhookA, webhookB) => {
      const keyA = webhookA.uri + (webhookA.topics?.sort().join(',') ?? '')
      const keyB = webhookB.uri + (webhookB.topics?.sort().join(',') ?? '')
      return keyA.localeCompare(keyB)
    })
  }

  const diffConfigContent = buildDiffConfigContent(baselineConfig, remoteConfig, app.configSchema)

  // List of field included in the config except the ones that only affect the CLI and are not pushed to the server
  // (versioned fields)
  const versionedLocalFieldNames = filterNonVersionedAppFields(baselineConfig)
  // List of remote fields that have different values to the local ones or are not present in the local config
  const remoteDiffModifications = diffConfigContent
    ? getFieldsFromDiffConfigContent(diffConfigContent.baselineContent)
    : []
  // List of local fields that have different values to the remote ones or  are not present in the remote config
  const localDiffModifications = diffConfigContent
    ? getFieldsFromDiffConfigContent(diffConfigContent.updatedContent)
    : []
  // List of versioned field that exists locally and remotely and have the same value
  const notModifiedVersionedLocalFieldNames = versionedLocalFieldNames.filter(
    (field) => !remoteDiffModifications.includes(field) && !localDiffModifications.includes(field),
  )
  // List of versioned field that exists locally and remotely and have different values
  const modifiedVersionedLocalFieldNames = versionedLocalFieldNames.filter(
    (field) => remoteDiffModifications.includes(field) && localDiffModifications.includes(field),
  )
  // List of versioned field that exists locally but not remotely
  const newVersionedLocalFieldNames = localDiffModifications.filter(
    (field) => !remoteDiffModifications.includes(field) && versionedLocalFieldNames.includes(field),
  )
  // List of versioned field that exists remotely but not locally
  // `handle` property won't be temporary shown in the list of removed properties
  const deletedVersionedLocalFieldNames = remoteDiffModifications.filter(
    (field) => !localDiffModifications.includes(field) && field !== 'handle',
  )

  return {
    existingFieldNames: notModifiedVersionedLocalFieldNames,
    existingUpdatedFieldNames: modifiedVersionedLocalFieldNames,
    newFieldNames: newVersionedLocalFieldNames,
    deletedFieldNames: deletedVersionedLocalFieldNames,
  }
}

function buildDiffConfigContent(
  localConfig: CurrentAppConfiguration,
  remoteConfig: Partial<AppConfigurationUsedByCli>,
  schema: zod.ZodTypeAny,
) {
  const [updated, baseline] = deepDifference(
    {...(rewriteConfiguration(schema, localConfig) as object), build: undefined},
    {...(rewriteConfiguration(schema, remoteConfig) as object), build: undefined},
  )

  if (deepCompare(updated, baseline)) {
    return undefined
  }

  return {
    baselineContent: encodeToml(baseline),
    updatedContent: encodeToml(updated),
  }
}

/**
 * This method extracts the list of global fields or global sections from the string that represents a toml section like
 * this:
 *        embedded = true
 *
 *        [access_scopes]
 *        scopes = "read_products,write_products,write_discounts"
 *
 *        [webhooks.privacy_compliance]
 *        customer_deletion_url = "https://myhooks.dev/apps/customer_deletion_url_edited"
 *
 * Each block is separated by a breaking line. The method will the extract
 * the `field`  following these patterns:
 * - <field> = <value> (in this case all the fields inside the block that matches the pattern will be returned)
 * - [<field>]
 * - [\<field.subsection\>]
 *
 * @param diffConfigContent - The toml string to parse
 * @returns The list of fields
 */
function getFieldsFromDiffConfigContent(diffConfigContent: string): string[] {
  const fields = diffConfigContent
    // Split the input string into sections by one or more blank lines
    .split(/\n\s*\n/)
    .flatMap((section) => {
      // Split each section into lines
      const lines = section.split('\n')
      if (lines.length === 0) return []
      // Match the first line of the section against a regular expression to extract the first field name based on the
      // described patterns
      const firstLineMatch = lines[0]!.match(/^(?:\[(\w+)|(\w+)\s*=)/)
      if (!firstLineMatch) return []
      // Extract the first field name from the appropriate capture group
      const firstFieldName = firstLineMatch[1] || firstLineMatch[2]
      if (!firstFieldName) return []
      // Return field if matches either the pattern [\<field.subsection\>] or [<field>]
      if (firstFieldName.includes('.')) return [firstFieldName.split('.')[0]]
      // If the first line of the section matches the pattern  <field> = <value> extract the following
      // <field> = <value>  that match that condition until the section is finished
      const otherFieldNames = firstLineMatch[2]
        ? lines
            .slice(1)
            .map((line) => line.match(/^(\w+)\s*=/))
            .filter(Boolean)
            .map((match) => match![1])
        : []
      return [firstFieldName, ...otherFieldNames]
    })
    .filter((match): match is string => match !== undefined)

  // Return the list of fields without duplicates
  return Array.from(new Set(fields))
}

function loadLocalExtensionsIdentifiersBreakdown({
  validMatches: localRegistration,
  extensionsToCreate: localSourceToCreate,
  dashboardOnlyExtensions,
}: {
  validMatches: IdentifiersExtensions
  extensionsToCreate: LocalSource[]
  dashboardOnlyExtensions: RemoteSource[]
}): ExtensionIdentifiersBreakdown {
  const identifiersToUpdate = Object.keys(localRegistration).map((identifier) =>
    buildExtensionBreakdownInfo(identifier, undefined),
  )
  const identifiersToCreate = localSourceToCreate.map((source) =>
    buildExtensionBreakdownInfo(source.localIdentifier, undefined),
  )
  const dashboardToUpdate = dashboardOnlyExtensions
    .filter((dashboard) => !Object.values(localRegistration).includes(dashboard.uuid))
    .map((dashboard) => buildDashboardBreakdownInfo(dashboard.title))
  return {
    onlyRemote: [] as ExtensionIdentifierBreakdownInfo[],
    toCreate: [] as ExtensionIdentifierBreakdownInfo[],
    toUpdate: [] as ExtensionIdentifierBreakdownInfo[],
    unchanged: [...identifiersToUpdate, ...identifiersToCreate, ...dashboardToUpdate],
  }
}

async function resolveRemoteExtensionIdentifiersBreakdown(
  developerPlatformClient: DeveloperPlatformClient,
  remoteApp: MinimalOrganizationApp,
  validMatches: IdentifiersExtensions,
  toCreate: LocalSource[],
  dashboardOnly: RemoteSource[],
  specs: ExtensionSpecification[],
  activeAppVersion?: AppVersion,
): Promise<ExtensionIdentifiersBreakdown | undefined> {
  const version = activeAppVersion || (await developerPlatformClient.activeAppVersion(remoteApp))
  if (!version) return

  const extensionIdentifiersBreakdown = loadExtensionsIdentifiersBreakdown(
    version,
    validMatches,
    toCreate,
    specs,
    developerPlatformClient,
  )

  const dashboardOnlyFinal = dashboardOnly.filter(
    (dashboardOnly) =>
      !Object.values(validMatches).includes(dashboardOnly.uuid) &&
      !toCreate.map((source) => source.localIdentifier).includes(dashboardOnly.uuid),
  )
  const dashboardIdentifiersBreakdown = loadDashboardIdentifiersBreakdown(dashboardOnlyFinal, version)

  return {
    onlyRemote: [...extensionIdentifiersBreakdown.onlyRemote, ...dashboardIdentifiersBreakdown.onlyRemote],
    toCreate: [...extensionIdentifiersBreakdown.toCreate, ...dashboardIdentifiersBreakdown.toCreate],
    toUpdate: [...extensionIdentifiersBreakdown.toUpdate, ...dashboardIdentifiersBreakdown.toUpdate],
    unchanged: [...extensionIdentifiersBreakdown.unchanged, ...dashboardIdentifiersBreakdown.unchanged],
  }
}

function loadExtensionsIdentifiersBreakdown(
  activeAppVersion: AppVersion,
  validMatches: IdentifiersExtensions,
  toCreate: LocalSource[],
  specs: ExtensionSpecification[],
  developerPlatformClient: DeveloperPlatformClient,
) {
  const extensionModules = activeAppVersion?.appModuleVersions.filter(
    (ext) => extensionTypeStrategy(specs, ext.specification?.identifier) === 'uuid',
  )

  // In AppManagement, matching has to be via UID, but we acccept UUID matches if the UID is empty (migration pending)
  // In Partners, we keep the legacy match of only UUID.
  function moduleHasUUIDorUID(module: AppModuleVersion, identifier: string) {
    const UuidMatch = module.registrationUuid === identifier
    const UidMatch = module.registrationId === identifier
    const pendingMigration = module.registrationId.length === 0

    if (developerPlatformClient.supportsAtomicDeployments) {
      return UidMatch || (pendingMigration && UuidMatch)
    } else {
      return UuidMatch
    }
  }

  const allExistingExtensions = Object.entries(validMatches)
    .filter(([_identifier, uuid]) => extensionModules.some((module) => moduleHasUUIDorUID(module, uuid)))
    .map(([identifier, _uuid]) => identifier)

  // If registationId is empty, it means the extension doesn't have a UID yet, so this deploy will create one.
  const extensionsBeingMigratedToDevDash = extensionModules.filter((module) => module.registrationId === '')
  const extensionsToUpdate = allExistingExtensions.filter((identifier) =>
    extensionsBeingMigratedToDevDash.some((module) => module.registrationUuid === validMatches[identifier]),
  )

  const unchangedExtensions = allExistingExtensions.filter(
    (identifier) =>
      !extensionsBeingMigratedToDevDash.some((module) => module.registrationUuid === validMatches[identifier]),
  )

  const extensionsToCreate = Object.entries(validMatches)
    .filter(([_identifier, uuid]) => !extensionModules.some((module) => moduleHasUUIDorUID(module, uuid)))
    .map(([identifier, uuid]) => ({title: identifier, uid: uuid}))
  const originalToCreate = toCreate.map((source) => ({title: source.localIdentifier, uid: source.uid}))

  originalToCreate.forEach((source) => {
    const index = extensionsToCreate.findIndex((extension) => extension.title === source.title)
    index === -1 ? extensionsToCreate.push(source) : (extensionsToCreate[index] = source)
  })

  const extensionsOnlyRemote = extensionModules
    .filter(
      (module) =>
        !Object.values(validMatches).some((uuid) => moduleHasUUIDorUID(module, uuid)) &&
        !toCreate.map((source) => source.localIdentifier).some((identifier) => moduleHasUUIDorUID(module, identifier)),
    )
    .map((module) => ({title: module.registrationTitle, uid: module.registrationId}))

  return {
    onlyRemote: extensionsOnlyRemote.map(({title, uid}) => buildExtensionBreakdownInfo(title, uid)),
    toCreate: extensionsToCreate.map(({title, uid}) => buildExtensionBreakdownInfo(title, uid)),
    toUpdate: extensionsToUpdate.map((title) => buildExtensionBreakdownInfo(title, undefined)),
    unchanged: unchangedExtensions.map((title) => buildExtensionBreakdownInfo(title, undefined)),
  }
}

function loadDashboardIdentifiersBreakdown(currentRegistrations: RemoteSource[], activeAppVersion: AppVersion) {
  const currentVersions =
    activeAppVersion?.appModuleVersions.filter(
      (module) => module.specification!.options.managementExperience === 'dashboard',
    ) || []

  const versionsNotIncluded = (version: AppModuleVersion) =>
    !currentRegistrations.map((registration) => registration.uuid).includes(version.registrationUuid!)
  const onlyRemote = currentVersions
    .filter(versionsNotIncluded)
    .map((module) => buildDashboardBreakdownInfo(module.registrationTitle))

  const registrationIncluded = (registration: RemoteSource) =>
    currentVersions.map((version) => version.registrationUuid).includes(registration.uuid)
  const registrationNotIncluded = (registration: RemoteSource) => !registrationIncluded(registration)
  const toCreate = currentRegistrations
    .filter(registrationNotIncluded)
    .map((registration) => buildDashboardBreakdownInfo(registration.title))
  const toUpdate = currentRegistrations
    .filter(registrationIncluded)
    .map((registration) => buildDashboardBreakdownInfo(registration.title))

  return {
    onlyRemote,
    toCreate,
    toUpdate: [],
    unchanged: toUpdate,
  }
}
